import React, { Fragment, useEffect, useState } from 'react';
import {
    Row, Col, Typography, Button, Modal, Form, Input, Select, Switch,
    Flex, Tag, Divider, Card, Table, Space,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import EditIcon from '../../components/icon/EditIcon';
import DeleteIcon from '../../components/icon/DeleteIcon';
import PlusIcon from '../../components/icon/PlusIcon';
import openNotification from '../../components/notification';
import { getCategories, createCategory, updateCategory, getCustomFields, createCustomField } from './redux/slice';
import { primary, secondary, muted } from '../../utils/theme/config';

const { Title, Text } = Typography;

const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Zahl' },
    { value: 'date', label: 'Datum' },
    { value: 'boolean', label: 'Ja/Nein' },
    { value: 'select', label: 'Auswahl' },
    { value: 'multiselect', label: 'Mehrfachauswahl' },
    { value: 'currency', label: 'Währung (€)' },
    { value: 'url', label: 'URL' },
    { value: 'email', label: 'E-Mail' },
    { value: 'phone', label: 'Telefon' },
];

const slugify = (str) =>
    str
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

const ContractCategories = () => {
    const [categoryForm] = Form.useForm();
    const [fieldForm] = Form.useForm();
    const dispatch = useDispatch();
    const auth = useSelector(state => state.auth);
    const contracts = useSelector(state => state.contracts);
    const { categories, customFields, loading } = contracts;
    const organizer = auth?.decodeToken?.organizer;

    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [fieldOptions, setFieldOptions] = useState([]);
    const [selectedFieldType, setSelectedFieldType] = useState(null);
    const [optionInput, setOptionInput] = useState('');

    useEffect(() => {
        if (organizer) {
            dispatch(getCategories({ organizer }));
        }
    }, [organizer]);

    useEffect(() => {
        if (selectedCategory) {
            dispatch(getCustomFields({ category: selectedCategory._id, organizer }));
        }
    }, [selectedCategory]);

    const handleOpenAddCategory = () => {
        setEditingCategory(null);
        categoryForm.resetFields();
        setCategoryModalOpen(true);
    };

    const handleOpenEditCategory = (cat) => {
        setEditingCategory(cat);
        categoryForm.setFieldsValue({ name: cat.name, description: cat.description, color: cat.color });
        setCategoryModalOpen(true);
    };

    const handleCategorySubmit = (values) => {
        if (editingCategory) {
            dispatch(updateCategory({ categoryData: { ...values, id: editingCategory._id, organizer }, organizer }));
        } else {
            dispatch(createCategory({ categoryData: { ...values, organizer }, organizer }));
        }
        setCategoryModalOpen(false);
        categoryForm.resetFields();
        setEditingCategory(null);
    };

    const handleAddOption = () => {
        if (!optionInput.trim()) return;
        setFieldOptions((prev) => [...prev, { label: optionInput.trim(), value: slugify(optionInput.trim()) }]);
        setOptionInput('');
    };

    const handleRemoveOption = (idx) => {
        setFieldOptions((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleFieldSubmit = (values) => {
        if (!selectedCategory) {
            openNotification({ message: 'Bitte zuerst eine Kategorie auswählen.', type: 'error' });
            return;
        }
        const fieldKey = values.fieldKey || slugify(values.fieldName);
        const payload = {
            ...values,
            fieldKey,
            category: selectedCategory._id,
            organizer,
            options: ['select', 'multiselect'].includes(values.fieldType) ? fieldOptions : [],
        };
        dispatch(createCustomField({ fieldData: payload, category: selectedCategory._id, organizer }));
        fieldForm.resetFields();
        setFieldOptions([]);
        setSelectedFieldType(null);
    };

    const categoryColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <Flex align='center' gap={8}>
                    {record.color && (
                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: record.color, flexShrink: 0 }} />
                    )}
                    <Text>{name}</Text>
                    {record.isSystem && <Tag>System</Tag>}
                </Flex>
            ),
        },
        {
            title: 'Beschreibung',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Felder',
            dataIndex: 'customFieldCount',
            key: 'customFieldCount',
            width: 80,
            render: (count) => count ?? 0,
        },
        {
            title: 'Aktion',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Flex gap={4}>
                    <Button
                        type='text'
                        className='padding-1'
                        onClick={() => handleOpenEditCategory(record)}
                        disabled={record.isSystem}
                    >
                        <EditIcon role='button' />
                    </Button>
                </Flex>
            ),
        },
    ];

    const fieldColumns = [
        {
            title: 'Feldname',
            dataIndex: 'fieldName',
            key: 'fieldName',
        },
        {
            title: 'Feldschlüssel',
            dataIndex: 'fieldKey',
            key: 'fieldKey',
            render: (val) => <Text code>{val}</Text>,
        },
        {
            title: 'Typ',
            dataIndex: 'fieldType',
            key: 'fieldType',
            render: (val) => FIELD_TYPES.find((t) => t.value === val)?.label || val,
        },
        {
            title: 'Pflicht',
            dataIndex: 'required',
            key: 'required',
            render: (val) => val ? <Tag color={primary}>Ja</Tag> : <Tag color={muted}>Nein</Tag>,
        },
    ];

    return (
        <Fragment>
            <Row justify='space-between' align='middle' className='bg-white padding-1 rounded-1' gutter={[16, 16]}>
                <Col>
                    <Title level={4} type='success'>Kategorien & Felder</Title>
                </Col>
                <Col>
                    <Button type='primary' onClick={handleOpenAddCategory} icon={<PlusIcon />}>
                        Kategorie hinzufügen
                    </Button>
                </Col>
            </Row>

            <Row gutter={[16, 16]} className='padding-top-1'>
                {/* Left Panel: Category List */}
                <Col xxl={10} xl={10} lg={12} md={24} sm={24} xs={24}>
                    <Card title={<Text strong>Kategorien</Text>} className='border-green'>
                        <Table
                            columns={categoryColumns}
                            dataSource={categories}
                            rowKey={(r) => r._id}
                            pagination={false}
                            loading={loading}
                            size='small'
                            onRow={(record) => ({
                                onClick: () => setSelectedCategory(record),
                                style: {
                                    cursor: 'pointer',
                                    background: selectedCategory?._id === record._id ? '#e5f1ec' : undefined,
                                },
                            })}
                        />
                    </Card>
                </Col>

                {/* Right Panel: Custom Fields */}
                <Col xxl={14} xl={14} lg={12} md={24} sm={24} xs={24}>
                    {selectedCategory ? (
                        <Card
                            title={
                                <Flex align='center' gap={8}>
                                    <Text strong>Felder für:</Text>
                                    <Text style={{ color: primary }}>{selectedCategory.name}</Text>
                                </Flex>
                            }
                            className='border-green'
                        >
                            <Table
                                columns={fieldColumns}
                                dataSource={customFields}
                                rowKey={(r) => r._id || r.fieldKey}
                                pagination={false}
                                loading={loading}
                                size='small'
                            />

                            <Divider />
                            <Title level={5} type='success'>Neues Feld hinzufügen</Title>
                            <Form
                                form={fieldForm}
                                layout='vertical'
                                onFinish={handleFieldSubmit}
                            >
                                <Row gutter={12}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='fieldName' label='Feldname' rules={[{ required: true, message: 'Pflichtfeld' }]}>
                                            <Input
                                                placeholder='z.B. Versicherungsnummer'
                                                onChange={(e) => {
                                                    fieldForm.setFieldValue('fieldKey', slugify(e.target.value));
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='fieldKey' label='Feldschlüssel'>
                                            <Input placeholder='auto_generiert' />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='fieldType' label='Feldtyp' rules={[{ required: true, message: 'Pflichtfeld' }]}>
                                            <Select
                                                placeholder='Typ wählen'
                                                options={FIELD_TYPES}
                                                onChange={(val) => { setSelectedFieldType(val); setFieldOptions([]); }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='required' label='Pflichtfeld' valuePropName='checked'>
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {['select', 'multiselect'].includes(selectedFieldType) && (
                                    <div style={{ marginBottom: 16 }}>
                                        <Text>Optionen:</Text>
                                        <Flex gap={8} wrap style={{ marginTop: 8, marginBottom: 8 }}>
                                            {fieldOptions.map((opt, idx) => (
                                                <Tag
                                                    key={idx}
                                                    closable
                                                    onClose={() => handleRemoveOption(idx)}
                                                >
                                                    {opt.label}
                                                </Tag>
                                            ))}
                                        </Flex>
                                        <Flex gap={8}>
                                            <Input
                                                placeholder='Option eingeben'
                                                value={optionInput}
                                                onChange={(e) => setOptionInput(e.target.value)}
                                                onPressEnter={handleAddOption}
                                                style={{ maxWidth: 240 }}
                                            />
                                            <Button onClick={handleAddOption}>Hinzufügen</Button>
                                        </Flex>
                                    </div>
                                )}

                                <Button type='primary' htmlType='submit' loading={loading}>
                                    Feld speichern
                                </Button>
                            </Form>
                        </Card>
                    ) : (
                        <Card className='border-green'>
                            <Flex justify='center' align='center' style={{ minHeight: 200 }}>
                                <Text type='secondary'>Bitte eine Kategorie auswählen, um Felder zu verwalten.</Text>
                            </Flex>
                        </Card>
                    )}
                </Col>
            </Row>

            {/* Category Modal */}
            <Modal
                open={categoryModalOpen}
                title={
                    <Fragment>
                        <Title level={4} type='success'>{editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</Title>
                        <Divider />
                    </Fragment>
                }
                footer={null}
                onCancel={() => { setCategoryModalOpen(false); categoryForm.resetFields(); setEditingCategory(null); }}
            >
                <Form
                    form={categoryForm}
                    layout='vertical'
                    onFinish={handleCategorySubmit}
                >
                    <Form.Item name='name' label='Name' rules={[{ required: true, message: 'Name ist erforderlich' }]}>
                        <Input placeholder='Kategoriename' />
                    </Form.Item>
                    <Form.Item name='description' label='Beschreibung'>
                        <Input placeholder='Kurze Beschreibung' />
                    </Form.Item>
                    <Form.Item name='color' label='Farbe'>
                        <input type='color' style={{ width: '100%', height: 36, border: '1px solid #99C6B4', borderRadius: 8, cursor: 'pointer' }} />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Button block danger type='primary' htmlType='button' onClick={() => { setCategoryModalOpen(false); categoryForm.resetFields(); }}>
                                Abbrechen
                            </Button>
                        </Col>
                        <Col span={12}>
                            <Button block type='primary' htmlType='submit' loading={loading}>
                                {editingCategory ? 'Aktualisieren' : 'Speichern'}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Fragment>
    );
};

export default ContractCategories;
